import type { SwitchClientResponse } from '../models/auth-api.model';
import { unwrapResourceBody } from './api-envelope.util';
import { isRecord } from './api-error.util';

/** JWT strings must not be trimmed or altered — only strip surrounding whitespace from HTTP JSON. */
function pickJwtString(obj: Record<string, unknown>, ...keys: string[]): string | null {
  for (const key of keys) {
    const v = obj[key];
    if (typeof v === 'string' && v.length > 0) {
      return v;
    }
  }
  return null;
}

function pickNumber(obj: Record<string, unknown>, ...keys: string[]): number | null {
  for (const key of keys) {
    const v = obj[key];
    if (typeof v === 'number' && Number.isFinite(v)) {
      return v;
    }
    if (typeof v === 'string' && v.trim()) {
      const n = Number(v);
      if (Number.isFinite(n)) {
        return n;
      }
    }
  }
  return null;
}

/**
 * Normalizes switch-client API body (`{ accessToken, expiresIn }` or `{ data: … }`, camel/snake).
 */
export function normalizeSwitchClientResponse(body: unknown): SwitchClientResponse {
  const raw = unwrapResourceBody<unknown>(body);
  if (!isRecord(raw)) {
    throw new Error('Invalid switch-client response body');
  }
  const accessToken = pickJwtString(raw, 'accessToken', 'access_token');
  const expiresIn = pickNumber(raw, 'expiresIn', 'expires_in');
  if (!accessToken || accessToken.split('.').length < 3) {
    throw new Error('Invalid switch-client response: missing access token');
  }
  if (expiresIn == null || expiresIn <= 0) {
    throw new Error('Invalid switch-client response: missing expiresIn');
  }
  return { accessToken, expiresIn };
}
